import Cookies from 'js-cookie';

// Function to parse JWT and check if it's expired
export const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
        .join('')
    );

    const { exp } = JSON.parse(jsonPayload);
    const currentTime = Date.now() / 1000;
    
    // Add a buffer of 30 seconds to prevent edge cases
    return exp < currentTime + 30;
  } catch (error) {
    console.error('Error parsing JWT token:', error);
    return true;
  }
};

// Create a promise to track ongoing token refresh
let refreshTokenPromise = null;

// Function to refresh the token
export const refreshToken = async () => {
  // If there's already a refresh in progress, return that promise instead of starting a new refresh
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }
  
  // Create a new refresh token promise
  refreshTokenPromise = (async () => {
    try {
      const refreshToken = localStorage.getItem('refreshToken');
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      
      console.log("Starting token refresh");
      
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URI}user/refresh?refreshToken=${refreshToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to refresh token');
      }
      
      const data = await response.json();
      
      // Update cookies and localStorage
      Cookies.set('accessToken', data.token);
      localStorage.setItem('refreshToken', data.refreshToken);
      
      // If there's a user object in localStorage, update it
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      if (user) {
        user.accessToken = data.token;
        user.refreshToken = data.refreshToken;
        localStorage.setItem('user', JSON.stringify(user));
      }
      
      console.log("Token refresh successful");
      return data.token;
    } catch (error) {
      console.error('Token refresh failed:', error);
      // Clear auth data on refresh failure
      Cookies.remove('accessToken');
      Cookies.remove('adminKey');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('user');
      
      // Redirect to login page
      window.location.href = '/login';
      throw error;
    } finally {
      // Reset the promise after completion (success or failure)
      setTimeout(() => {
        refreshTokenPromise = null;
      }, 200); // Small delay to prevent race conditions
    }
  })();
  
  return refreshTokenPromise;
};

// Fetch wrapper with token refresh
export const authenticatedFetch = async (url, options = {}) => {
  let accessToken = Cookies.get('accessToken');
  let originalUrl = url;
  
  // Check if token is expired
  if (isTokenExpired(accessToken)) {
    try {
      accessToken = await refreshToken();
    } catch (error) {
      // refreshToken function already handles the redirect to login
      throw error;
    }
  }
  
  // Prepare headers
  const headers = {
    ...options.headers,
    'Authorization': `Bearer ${accessToken}`
  };

  // Modify URL if needed
  if (!url.includes('?accessToken=')) {
    // Check if URL already has parameters
    url = url + (url.includes('?') ? '&' : '?') + `accessToken=${accessToken}`;
  }
  
  // Make the actual request
  try {
    const response = await fetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });
    
    // If we got a 401 even after refresh attempt, retry once more
    if (response.status === 401) {
      // Check if this is already a retry attempt
      if (options._isRetry) {
        Cookies.remove('accessToken');
        Cookies.remove('adminKey');
        localStorage.removeItem('refreshToken');
        localStorage.removeItem('user');
        window.location.href = '/login';
        throw new Error('Authentication failed');
      }
      
      // Force token refresh and retry the request once
      try {
        // Clear the existing token to force a refresh
        Cookies.remove('accessToken');
        
        // Wait a moment before retrying to avoid race conditions
        await new Promise(resolve => setTimeout(resolve, 300));
        
        // Retry the request with a fresh token
        return authenticatedFetch(originalUrl, { 
          ...options, 
          _isRetry: true 
        });
      } catch (error) {
        throw new Error('Authentication failed');
      }
    }
    
    return response;
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
};

export const preloadAuthToken = async () => {
  const accessToken = Cookies.get('accessToken');
  
  // Only refresh if token is expired or missing
  if (isTokenExpired(accessToken)) {
    try {
      // Check if we have a refresh token before attempting refresh
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        await refreshToken();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Failed to preload auth token:", error);
      return false;
    }
  }
  
  return true; // Valid token already exists
};